import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api, { fmtINR, fmtNum } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Save, Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { logWarn } from '@/lib/log';

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const r3 = (n) => Math.round((Number(n) || 0) * 1000) / 1000;

// Stable row identity for dynamic (addable/removable) lists — avoids index-as-key bugs.
const uid = () => `r-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
const DYNAMIC_ROW_KEYS = ['lubes', 'settlements', 'credit_sales', 'srps_bills', 'mfuel_bills', 'fleet_card_txns', 'pos_txns', 'payments_received', 'exp_details'];
const withRowUids = (data) => {
  DYNAMIC_ROW_KEYS.forEach((k) => { data[k] = (data[k] || []).map((row) => (row._uid ? row : { ...row, _uid: uid() })); });
  return data;
};
const stripRowUids = (data) => {
  DYNAMIC_ROW_KEYS.forEach((k) => { data[k] = (data[k] || []).map(({ _uid, ...row }) => row); });
  return data;
};

const blank = (msn = 12, hsdn = 12, cngn = 4) => ({
  operator_name: '',
  lubes: [], lube_total_amount: 0,
  tank_decant: [{ tank: 'MS 20 KL 1', dip: 0, litres: 0 }, { tank: 'MS 20 KL 2', dip: 0, litres: 0 }, { tank: 'HSD 45 KL', dip: 0, litres: 0 }],
  ms_nozzles: Array.from({ length: msn }, (_, i) => ({ nozzle_no: i + 1, opening: 0, closing: 0, sale: 0 })),
  ms_totals: { total_sale: 0, testing: 0, net_sale: 0, rate: 0, amount: 0 },
  hsd_nozzles: Array.from({ length: hsdn }, (_, i) => ({ nozzle_no: i + 1, opening: 0, closing: 0, sale: 0 })),
  hsd_totals: { total_sale: 0, testing: 0, net_sale: 0, rate: 0, amount: 0 },
  gantry_nozzles: [{ nozzle_no: 1, opening: 0, closing: 0, sale: 0 }],
  gantry_totals: { total_sale: 0, testing: 0, net_sale: 0, rate: 0, amount: 0 },
  cng_nozzles: Array.from({ length: cngn }, (_, i) => ({ nozzle_no: i + 1, opening: 0, closing: 0, sale: 0 })),
  cng_totals: { total_sale: 0, testing: 0, net_sale: 0, rate: 0, amount: 0 },
  settlements: ['BPCL', 'HDFC', 'AMEX', 'SBI POINT REDEEM', 'PAYTM', 'PHONE PAY', 'U FILL', 'EXP', 'GANTRY'].map((m) => ({ mode: m, amount: 0 })),
  credit_sales: [], total_credit_sale: 0,
  bowser_readings: [{ name: 'SRPS', opening: 0, closing: 0, sale: 0, rate: 0, amount: 0 }, { name: 'M FUEL', opening: 0, closing: 0, sale: 0, rate: 0, amount: 0 }],
  bowser_total: { sale: 0, amount: 0 },
  srps_bills: [], mfuel_bills: [], loss_gain: 0,
  fleet_card_txns: [], fleet_card_total: 0, pos_txns: [], pos_total: 0,
  ms_density: 0, hsd_density: 0,
  cash_summary: { deposit_in_bank: 0, cash_received: 0, net_amount: 0, total_cash_deposit: 0 },
  day_summary: { puc_sale: 0, lube: 0, total_amount: 0, credit_sale: 0, outstanding: 0, net_amount: 0 },
  payments_received: [],
  exp_details: [],
  denominations: [500, 200, 100, 50, 20, 10].map((d) => ({ denom: d, count: 0, amount: 0 })),
  denomination_total: 0,
});

const Num = ({ value, onChange, w = 'w-24', testId }) => (
  <Input type="number" step="any" value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
    className={`h-8 text-xs ${w}`} data-testid={testId} />
);

const PartyInput = ({ value, onChange, parties, testId }) => (
  <>
    <Input list="pump-party-list" value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="Party name…" className="h-8 text-xs min-w-[190px]" data-testid={testId} />
  </>
);

// Meter section (MS / HSD / GANTRY / CNG)
const MeterBlock = ({ title, nozzles, totals, onChange, prefix }) => {
  const setNoz = (i, field, v) => {
    const nz = nozzles.map((n, idx) => (idx === i ? { ...n, [field]: v } : n));
    nz[i].sale = r3(Math.max((nz[i].closing || 0) - (nz[i].opening || 0), 0));
    recompute(nz, totals);
  };
  const recompute = (nz, t) => {
    const total = r3(nz.reduce((s, n) => s + (n.sale || 0), 0));
    const net = r3(Math.max(total - (t.testing || 0), 0));
    onChange(nz, { ...t, total_sale: total, net_sale: net, amount: r2(net * (t.rate || 0)) });
  };
  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <div className="flex items-center justify-between mb-2">
        <p className="font-semibold text-sm">{title}</p>
        <div className="flex items-center gap-2 text-xs">
          <span>Testing</span><Num value={totals.testing} onChange={(v) => recompute(nozzles, { ...totals, testing: v })} w="w-20" testId={`${prefix}-testing`} />
          <span>Rate ₹</span><Num value={totals.rate} onChange={(v) => recompute(nozzles, { ...totals, rate: v })} w="w-20" testId={`${prefix}-rate`} />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-muted-foreground"><tr>{['Nozzle', 'Opening', 'Closing', 'Sale (L)'].map((h) => <th key={h} className="text-left px-2 py-1 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {nozzles.map((n, i) => (
              <tr key={`${prefix}-${n.nozzle_no}`} className="border-t border-border">
                <td className="px-2 py-1 font-medium">{n.nozzle_no}</td>
                <td className="px-2 py-1"><Num value={n.opening} onChange={(v) => setNoz(i, 'opening', v)} w="w-28" testId={`${prefix}-open-${i}`} /></td>
                <td className="px-2 py-1"><Num value={n.closing} onChange={(v) => setNoz(i, 'closing', v)} w="w-28" testId={`${prefix}-close-${i}`} /></td>
                <td className="px-2 py-1 font-semibold">{fmtNum(n.sale)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-xs border-t border-border pt-2" data-testid={`${prefix}-totals`}>
        <span>Total: <b>{fmtNum(totals.total_sale)} L</b></span>
        <span>Testing: <b>{fmtNum(totals.testing)} L</b></span>
        <span>Net: <b>{fmtNum(totals.net_sale)} L</b></span>
        <span>Amount: <b>{fmtINR(totals.amount)}</b></span>
      </div>
    </div>
  );
};

export default function PumpDailyReport() {
  const { date } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const base = loc.pathname.startsWith('/admin') ? '/admin/pump' : '/pump';
  const [rep, setRep] = useState(null);
  const [masters, setMasters] = useState({ products: [], parties: [], payment_modes: [] });
  const [meta, setMeta] = useState({ source: 'manual', edited: false, exists: false });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const m = await api.get('/pump/masters');
    setMasters(m.data);
    try {
      const r = await api.get(`/pump/reports/${date}`);
      const data = { ...blank(), ...r.data };
      if (!data.denominations || data.denominations.length === 0) data.denominations = blank().denominations;
      if (!data.day_summary || Object.keys(data.day_summary).length === 0) data.day_summary = blank().day_summary;
      setRep(withRowUids(data));
      setMeta({ source: r.data.source, edited: r.data.edited, exists: true });
    } catch (e) {
      const b = blank(m.data.ms_nozzle_count, m.data.hsd_nozzle_count, m.data.cng_nozzle_count);
      b.lubes = (m.data.products || []).map((p, i) => ({ sno: i + 1, product_name: p.name, opening: 0, received: 0, sale: 0, rate: 0, amount: 0, closing: 0 }));
      // Prefill: previous day's closing readings become today's opening readings
      try {
        const pf = (await api.get(`/pump/prefill/${date}`)).data;
        if (pf.found) {
          if (pf.ms_nozzles?.length) { b.ms_nozzles = pf.ms_nozzles; b.ms_totals = { ...b.ms_totals, rate: pf.ms_rate || 0 }; }
          if (pf.hsd_nozzles?.length) { b.hsd_nozzles = pf.hsd_nozzles; b.hsd_totals = { ...b.hsd_totals, rate: pf.hsd_rate || 0 }; }
          if (pf.gantry_nozzles?.length) { b.gantry_nozzles = pf.gantry_nozzles; b.gantry_totals = { ...b.gantry_totals, rate: pf.gantry_rate || 0 }; }
          if (pf.cng_nozzles?.length) { b.cng_nozzles = pf.cng_nozzles; b.cng_totals = { ...b.cng_totals, rate: pf.cng_rate || 0 }; }
          if (pf.lubes?.length) b.lubes = pf.lubes;
          if (pf.bowser_readings?.length) b.bowser_readings = pf.bowser_readings;
          if (pf.tank_decant?.length) b.tank_decant = pf.tank_decant;
          b.ms_density = pf.ms_density || 0;
          b.hsd_density = pf.hsd_density || 0;
          toast.info(`Opening readings prefilled from ${pf.prev_date} closing readings`);
        }
      } catch (pe) { logWarn('Prefill unavailable; starting with a blank form', pe); }
      setRep(withRowUids(b));
      setMeta({ source: 'manual', edited: false, exists: false });
    }
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const upd = (patch) => setRep((r) => ({ ...r, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const body = stripRowUids({ ...rep });
      body.lube_total_amount = r2(rep.lubes.reduce((s, l) => s + (l.amount || 0), 0));
      body.total_credit_sale = r2(rep.credit_sales.reduce((s, c) => s + (c.amount || 0), 0));
      body.bowser_total = { sale: r3(rep.bowser_readings.reduce((s, b) => s + (b.sale || 0), 0)), amount: r2(rep.bowser_readings.reduce((s, b) => s + (b.amount || 0), 0)) };
      body.fleet_card_total = r2(rep.fleet_card_txns.reduce((s, t) => s + (t.amount || 0), 0));
      body.pos_total = r2(rep.pos_txns.reduce((s, t) => s + (t.amount || 0), 0));
      await api.put(`/pump/reports/${date}`, body);
      toast.success(`Report saved for ${date}`);
      // Non-blocking denomination sanity check against Total Cash Deposit
      const denomTotal = (rep.denominations || []).reduce((s, x) => s + (x.amount || 0), 0);
      const deposit = Number(rep.cash_summary?.total_cash_deposit || 0);
      if (denomTotal > 0 && deposit > 0 && Math.abs(denomTotal - deposit) > 0.01) {
        toast.warning(`Denomination mismatch: notes total ₹${denomTotal.toLocaleString('en-IN')} ≠ cash deposit ₹${deposit.toLocaleString('en-IN')}`);
      }
      setMeta((m) => ({ ...m, exists: true, edited: true }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  if (!rep) return <div className="space-y-3"><Skeleton className="h-10 w-72" /><Skeleton className="h-96 w-full" /></div>;

  const setLube = (i, field, v) => {
    const ls = rep.lubes.map((l, idx) => (idx === i ? { ...l, [field]: v } : l));
    const l = ls[i];
    l.amount = r2((l.sale || 0) * (l.rate || 0));
    l.closing = r3((l.opening || 0) + (l.received || 0) - (l.sale || 0));
    upd({ lubes: ls });
  };
  const setBowser = (i, field, v) => {
    const bs = rep.bowser_readings.map((b, idx) => (idx === i ? { ...b, [field]: v } : b));
    const b = bs[i];
    b.sale = r3(Math.max((b.closing || 0) - (b.opening || 0), 0));
    b.amount = r2(b.sale * (b.rate || 0));
    upd({ bowser_readings: bs });
  };
  const setBill = (key, i, field, v) => {
    const rows = rep[key].map((b, idx) => (idx === i ? { ...b, [field]: v } : b));
    const b = rows[i];
    b.amount = r2((b.qty || 0) * (b.rate || 0));
    upd({ [key]: rows });
  };

  const creditTotal = r2(rep.credit_sales.reduce((s, c) => s + (c.amount || 0), 0));
  const lubeTotal = r2(rep.lubes.reduce((s, l) => s + (l.amount || 0), 0));

  // Report provenance label (avoids a nested ternary in JSX)
  let metaLabel = 'new entry';
  if (meta.exists) metaLabel = meta.edited ? 'edited' : meta.source;

  return (
    <div className="space-y-4">
      <datalist id="pump-party-list">{masters.parties.map((p) => <option key={p.id} value={p.name} />)}</datalist>
      <datalist id="pump-product-list">{masters.products.map((p) => <option key={p.id} value={p.name} />)}</datalist>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => nav(base)} data-testid="pump-back-button"><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
          <div>
            <h2 className="font-display text-lg font-semibold">Daily Sale Report — {date}</h2>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="outline" className="text-[10px]">{metaLabel}</Badge>
              <span>Operator</span>
              <Input value={rep.operator_name} onChange={(e) => upd({ operator_name: e.target.value })} className="h-7 w-36 text-xs" placeholder="Name" data-testid="pump-operator-input" />
            </div>
          </div>
        </div>
        <Button onClick={save} disabled={saving} data-testid="pump-save-button">{saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}Save Report</Button>
      </div>

      <Tabs defaultValue="meters">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="meters" data-testid="tab-meters">Fuel Meters</TabsTrigger>
          <TabsTrigger value="lubes" data-testid="tab-lubes">Lube Stock</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments & Credit</TabsTrigger>
          <TabsTrigger value="cards" data-testid="tab-cards">Card Txns</TabsTrigger>
        </TabsList>

        <TabsContent value="meters" className="space-y-4 mt-3">
          <div className="rounded-xl border border-border bg-card p-3">
            <p className="font-semibold text-sm mb-2">Tank Decant (Dip) & Density</p>
            <div className="flex flex-wrap gap-4">
              {rep.tank_decant.map((tk, i) => (
                <div key={tk.tank} className="flex items-center gap-2 text-xs">
                  <span className="font-medium">{tk.tank}</span>
                  <span>Dip</span><Num value={tk.dip} onChange={(v) => upd({ tank_decant: rep.tank_decant.map((x, idx) => idx === i ? { ...x, dip: v } : x) })} w="w-20" testId={`decant-dip-${i}`} />
                  <span>Litres</span><Num value={tk.litres} onChange={(v) => upd({ tank_decant: rep.tank_decant.map((x, idx) => idx === i ? { ...x, litres: v } : x) })} w="w-24" testId={`decant-litres-${i}`} />
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-4 mt-2 pt-2 border-t border-border text-xs items-center">
              <span className="flex items-center gap-1.5">MS Density <Num value={rep.ms_density} onChange={(v) => upd({ ms_density: v })} w="w-24" testId="ms-density-input" /></span>
              <span className="flex items-center gap-1.5">HSD Density <Num value={rep.hsd_density} onChange={(v) => upd({ hsd_density: v })} w="w-24" testId="hsd-density-input" /></span>
            </div>
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <MeterBlock title="MS (Petrol) Nozzles" prefix="ms" nozzles={rep.ms_nozzles} totals={rep.ms_totals} onChange={(n, t) => upd({ ms_nozzles: n, ms_totals: t })} />
            <MeterBlock title="HSD (Diesel) Nozzles" prefix="hsd" nozzles={rep.hsd_nozzles} totals={rep.hsd_totals} onChange={(n, t) => upd({ hsd_nozzles: n, hsd_totals: t })} />
            <MeterBlock title="Gantry" prefix="gantry" nozzles={rep.gantry_nozzles} totals={rep.gantry_totals} onChange={(n, t) => upd({ gantry_nozzles: n, gantry_totals: t })} />
            <MeterBlock title="CNG Dispensers" prefix="cng" nozzles={rep.cng_nozzles} totals={rep.cng_totals} onChange={(n, t) => upd({ cng_nozzles: n, cng_totals: t })} />
          </div>
          <div className="rounded-xl border border-border bg-card p-3">
            <p className="font-semibold text-sm mb-2">Bowser Readings (SRPS & M FUEL)</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-muted-foreground"><tr>{['Name', 'Opening', 'Closing', 'Sale (L)', 'Rate ₹', 'Amount ₹'].map((h) => <th key={h} className="text-left px-2 py-1 font-medium">{h}</th>)}</tr></thead>
                <tbody>
                  {rep.bowser_readings.map((b, i) => (
                    <tr key={b.name} className="border-t border-border">
                      <td className="px-2 py-1 font-medium">{b.name}</td>
                      <td className="px-2 py-1"><Num value={b.opening} onChange={(v) => setBowser(i, 'opening', v)} w="w-32" testId={`bowser-open-${i}`} /></td>
                      <td className="px-2 py-1"><Num value={b.closing} onChange={(v) => setBowser(i, 'closing', v)} w="w-32" testId={`bowser-close-${i}`} /></td>
                      <td className="px-2 py-1 font-semibold">{fmtNum(b.sale)}</td>
                      <td className="px-2 py-1"><Num value={b.rate} onChange={(v) => setBowser(i, 'rate', v)} w="w-20" testId={`bowser-rate-${i}`} /></td>
                      <td className="px-2 py-1 font-semibold">{fmtINR(b.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-4 text-sm mt-2 border-t border-border pt-2">
              <span>Total: <b>{fmtNum(rep.bowser_readings.reduce((s, b) => s + (b.sale || 0), 0))} L · {fmtINR(rep.bowser_readings.reduce((s, b) => s + (b.amount || 0), 0))}</b></span>
              <span className="flex items-center gap-1.5">Loss/Gain <Num value={rep.loss_gain} onChange={(v) => upd({ loss_gain: v })} w="w-24" testId="loss-gain" /></span>
            </div>
          </div>
          {[['srps_bills', 'SRPS Party Bills'], ['mfuel_bills', 'M FUEL Party Bills']].map(([key, title]) => (
            <div key={key} className="rounded-xl border border-border bg-card p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="font-semibold text-sm">{title}</p>
                <Button size="sm" variant="secondary" onClick={() => upd({ [key]: [...rep[key], { _uid: uid(), bill_no: '', party_name: '', qty: 0, freight: 0, rate: 0, amount: 0 }] })} data-testid={`add-${key}-row`}><Plus className="h-4 w-4 mr-1" />Row</Button>
              </div>
              {rep[key].map((b, i) => (
                <div key={b._uid || i} className="flex items-center gap-2 py-1 flex-wrap">
                  <Input value={b.bill_no} onChange={(e) => setBill(key, i, 'bill_no', e.target.value)} placeholder="Bill no" className="h-8 text-xs w-24" data-testid={`${key}-bill-${i}`} />
                  <PartyInput value={b.party_name} onChange={(v) => setBill(key, i, 'party_name', v)} parties={masters.parties} testId={`${key}-party-${i}`} />
                  <span className="text-xs">Qty</span><Num value={b.qty} onChange={(v) => setBill(key, i, 'qty', v)} w="w-24" testId={`${key}-qty-${i}`} />
                  <span className="text-xs">Freight</span><Num value={b.freight} onChange={(v) => setBill(key, i, 'freight', v)} w="w-20" testId={`${key}-freight-${i}`} />
                  <span className="text-xs">Rate</span><Num value={b.rate} onChange={(v) => setBill(key, i, 'rate', v)} w="w-20" testId={`${key}-rate-${i}`} />
                  <span className="text-xs font-semibold">{fmtINR(b.amount)}</span>
                  <button onClick={() => upd({ [key]: rep[key].filter((_, idx) => idx !== i) })} className="text-muted-foreground hover:text-destructive" aria-label="Remove" data-testid={`${key}-del-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              {rep[key].length === 0 && <p className="text-xs text-muted-foreground">No bills.</p>}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="lubes" className="mt-3">
          <div className="rounded-xl border border-border bg-card p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="font-semibold text-sm">Lube / DEF Stock Register</p>
              <Button size="sm" variant="secondary" onClick={() => upd({ lubes: [...rep.lubes, { _uid: uid(), sno: rep.lubes.length + 1, product_name: '', opening: 0, received: 0, sale: 0, rate: 0, amount: 0, closing: 0 }] })} data-testid="add-lube-row"><Plus className="h-4 w-4 mr-1" />Row</Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-muted-foreground"><tr>{['#', 'Product', 'Opening', 'Received', 'Sale', 'Rate ₹', 'Amount ₹', 'Closing', ''].map((h) => <th key={h} className="text-left px-2 py-1 font-medium">{h}</th>)}</tr></thead>
                <tbody>
                  {rep.lubes.map((l, i) => (
                    <tr key={l._uid || i} className="border-t border-border">
                      <td className="px-2 py-1">{i + 1}</td>
                      <td className="px-2 py-1"><Input list="pump-product-list" value={l.product_name} onChange={(e) => setLube(i, 'product_name', e.target.value)} className="h-8 text-xs min-w-[170px]" data-testid={`lube-name-${i}`} /></td>
                      <td className="px-2 py-1"><Num value={l.opening} onChange={(v) => setLube(i, 'opening', v)} w="w-20" testId={`lube-open-${i}`} /></td>
                      <td className="px-2 py-1"><Num value={l.received} onChange={(v) => setLube(i, 'received', v)} w="w-20" testId={`lube-recv-${i}`} /></td>
                      <td className="px-2 py-1"><Num value={l.sale} onChange={(v) => setLube(i, 'sale', v)} w="w-20" testId={`lube-sale-${i}`} /></td>
                      <td className="px-2 py-1"><Num value={l.rate} onChange={(v) => setLube(i, 'rate', v)} w="w-20" testId={`lube-rate-${i}`} /></td>
                      <td className="px-2 py-1 font-semibold">{fmtINR(l.amount)}</td>
                      <td className="px-2 py-1">{fmtNum(l.closing)}</td>
                      <td className="px-2 py-1"><button onClick={() => upd({ lubes: rep.lubes.filter((_, idx) => idx !== i) })} className="text-muted-foreground hover:text-destructive" aria-label="Remove row" data-testid={`lube-del-${i}`}><Trash2 className="h-3.5 w-3.5" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm mt-2 border-t border-border pt-2">Total Lube Amount: <b data-testid="lube-total">{fmtINR(lubeTotal)}</b></p>
          </div>
        </TabsContent>

        <TabsContent value="payments" className="space-y-4 mt-3">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="rounded-xl border border-border bg-card p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="font-semibold text-sm">Settlements (Payment Modes)</p>
                <Button size="sm" variant="secondary" onClick={() => upd({ settlements: [...rep.settlements, { _uid: uid(), mode: 'CASH', amount: 0 }] })} data-testid="add-settlement-row"><Plus className="h-4 w-4 mr-1" />Row</Button>
              </div>
              {rep.settlements.map((s, i) => (
                <div key={s._uid || i} className="flex items-center gap-2 py-1">
                  <Select value={s.mode} onValueChange={(v) => upd({ settlements: rep.settlements.map((x, idx) => idx === i ? { ...x, mode: v } : x) })}>
                    <SelectTrigger className="h-8 text-xs w-48" data-testid={`settle-mode-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{(masters.payment_modes || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                  <Num value={s.amount} onChange={(v) => upd({ settlements: rep.settlements.map((x, idx) => idx === i ? { ...x, amount: v } : x) })} w="w-32" testId={`settle-amt-${i}`} />
                  <button onClick={() => upd({ settlements: rep.settlements.filter((_, idx) => idx !== i) })} className="text-muted-foreground hover:text-destructive" aria-label="Remove" data-testid={`settle-del-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              <p className="text-sm mt-2 border-t border-border pt-2">Total Settlements: <b>{fmtINR(rep.settlements.reduce((s, x) => s + (x.amount || 0), 0))}</b></p>
            </div>
            <div className="rounded-xl border border-border bg-card p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="font-semibold text-sm">Credit Party Sales (RO)</p>
                <Button size="sm" variant="secondary" onClick={() => upd({ credit_sales: [...rep.credit_sales, { _uid: uid(), ledger_no: '', party_name: '', amount: 0, remark: '' }] })} data-testid="add-credit-row"><Plus className="h-4 w-4 mr-1" />Row</Button>
              </div>
              {rep.credit_sales.map((c, i) => (
                <div key={c._uid || i} className="flex items-center gap-2 py-1 flex-wrap">
                  <Input value={c.ledger_no} onChange={(e) => upd({ credit_sales: rep.credit_sales.map((x, idx) => idx === i ? { ...x, ledger_no: e.target.value } : x) })} placeholder="Ledger" className="h-8 text-xs w-20" data-testid={`credit-ledger-${i}`} />
                  <PartyInput value={c.party_name} onChange={(v) => upd({ credit_sales: rep.credit_sales.map((x, idx) => idx === i ? { ...x, party_name: v } : x) })} parties={masters.parties} testId={`credit-party-${i}`} />
                  <Num value={c.amount} onChange={(v) => upd({ credit_sales: rep.credit_sales.map((x, idx) => idx === i ? { ...x, amount: v } : x) })} w="w-28" testId={`credit-amt-${i}`} />
                  <button onClick={() => upd({ credit_sales: rep.credit_sales.filter((_, idx) => idx !== i) })} className="text-muted-foreground hover:text-destructive" aria-label="Remove" data-testid={`credit-del-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              <p className="text-sm mt-2 border-t border-border pt-2">Total Credit Sale: <b data-testid="credit-total">{fmtINR(creditTotal)}</b></p>
            </div>
          </div>
          <div className="rounded-xl border border-border bg-card p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="font-semibold text-sm">Payment Received by Companies</p>
              <Button size="sm" variant="secondary" onClick={() => upd({ payments_received: [...(rep.payments_received || []), { _uid: uid(), company: '', mode: 'FLEET CARD', amount: 0 }] })} data-testid="add-payment-received-row"><Plus className="h-4 w-4 mr-1" />Row</Button>
            </div>
            {(rep.payments_received || []).map((p, i) => (
              <div key={p._uid || i} className="flex items-center gap-2 py-1 flex-wrap">
                <Input value={p.company} onChange={(e) => upd({ payments_received: rep.payments_received.map((x, idx) => idx === i ? { ...x, company: e.target.value } : x) })} placeholder="Company name" className="h-8 text-xs w-56" data-testid={`payrec-company-${i}`} />
                <Select value={p.mode} onValueChange={(v) => upd({ payments_received: rep.payments_received.map((x, idx) => idx === i ? { ...x, mode: v } : x) })}>
                  <SelectTrigger className="h-8 text-xs w-40" data-testid={`payrec-mode-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{(masters.payment_modes || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
                <Num value={p.amount} onChange={(v) => upd({ payments_received: rep.payments_received.map((x, idx) => idx === i ? { ...x, amount: v } : x) })} w="w-28" testId={`payrec-amount-${i}`} />
                <button onClick={() => upd({ payments_received: rep.payments_received.filter((_, idx) => idx !== i) })} className="text-muted-foreground hover:text-destructive" aria-label="Remove" data-testid={`payrec-del-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            {(rep.payments_received || []).length === 0 && <p className="text-xs text-muted-foreground">No company payments received. Add a row.</p>}
            {(rep.payments_received || []).length > 0 && (
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs mt-2 border-t border-border pt-2" data-testid="payrec-totals">
                {Object.entries((rep.payments_received || []).reduce((acc, p) => { const m = p.mode || 'OTHER'; acc[m] = (acc[m] || 0) + (p.amount || 0); return acc; }, {})).map(([m, v]) => (
                  <span key={m}>{m}: <b>{fmtINR(v)}</b></span>
                ))}
                <span className="ml-auto">TOTAL: <b>{fmtINR((rep.payments_received || []).reduce((s, p) => s + (p.amount || 0), 0))}</b></span>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-xl border border-border bg-card p-3" data-testid="cash-deposit-block">
              <p className="font-semibold text-sm mb-2 bg-primary/10 text-primary rounded px-2 py-1 text-center">CASH DEPOSIT IN BANK</p>
              <div className="space-y-2 text-xs">
                <div className="flex items-center justify-between gap-2"><span className="font-medium">PUC SALE</span><Num value={rep.day_summary?.puc_sale} onChange={(v) => upd({ day_summary: { ...rep.day_summary, puc_sale: v } })} w="w-32" testId="daysum-puc_sale" /></div>
                <div className="flex items-center justify-between gap-2"><span className="font-medium">LUBE SALE</span><Num value={rep.day_summary?.lube} onChange={(v) => upd({ day_summary: { ...rep.day_summary, lube: v } })} w="w-32" testId="daysum-lube" /></div>
                <div className="flex items-center justify-between gap-2"><span className="font-medium">CASH RECEIVED BY COMPANIES</span><Num value={rep.cash_summary?.cash_received} onChange={(v) => upd({ cash_summary: { ...rep.cash_summary, cash_received: v } })} w="w-32" testId="cash-received-input" /></div>
                <div className="flex items-center justify-between gap-2"><span className="font-medium">NET AMOUNT</span><Num value={rep.cash_summary?.net_amount} onChange={(v) => upd({ cash_summary: { ...rep.cash_summary, net_amount: v } })} w="w-32" testId="cash-net-input" /></div>
                <div className="flex items-center justify-between gap-2 rounded bg-yellow-400/20 px-2 py-1.5"><span className="font-bold">TOTAL CASH DEPOSIT</span><Num value={rep.cash_summary?.total_cash_deposit} onChange={(v) => upd({ cash_summary: { ...rep.cash_summary, total_cash_deposit: v } })} w="w-32" testId="cash-total-deposit-input" /></div>
              </div>
            </div>
            <div className="rounded-xl border border-border bg-card p-3" data-testid="day-summary-block">
              <p className="font-semibold text-sm mb-2 bg-primary/10 text-primary rounded px-2 py-1 text-center">DAY SUMMARY</p>
              <div className="space-y-2 text-xs">
                {[['total_amount', 'TOTAL AMOUNT', true], ['credit_sale', 'CREDIT SALE', false], ['outstanding', 'OUTSTANDING', false], ['net_amount', 'NET AMOUNT', true]].map(([key, label, hl]) => (
                  <div key={key} className={`flex items-center justify-between gap-2 ${hl ? 'rounded bg-yellow-400/20 px-2 py-1.5' : ''}`}>
                    <span className={hl ? 'font-bold' : 'font-medium'}>{label}</span>
                    <Num value={rep.day_summary?.[key]} onChange={(v) => upd({ day_summary: { ...rep.day_summary, [key]: v } })} w="w-32" testId={`daysum-${key}`} />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-xl border border-border bg-card p-3" data-testid="exp-details-block">
              <div className="flex items-center justify-between mb-2">
                <p className="font-semibold text-sm">EXP DETAILS</p>
                <Button size="sm" variant="secondary" onClick={() => upd({ exp_details: [...(rep.exp_details || []), { _uid: uid(), amount: 0, note: '' }] })} data-testid="add-exp-row"><Plus className="h-4 w-4 mr-1" />Row</Button>
              </div>
              {(rep.exp_details || []).map((x, i) => (
                <div key={x._uid || i} className="flex items-center gap-2 py-1">
                  <Num value={x.amount} onChange={(v) => upd({ exp_details: rep.exp_details.map((e, idx) => idx === i ? { ...e, amount: v } : e) })} w="w-28" testId={`exp-amount-${i}`} />
                  <Input value={x.note} onChange={(e) => upd({ exp_details: rep.exp_details.map((y, idx) => idx === i ? { ...y, note: e.target.value } : y) })} placeholder="Expense detail" className="h-8 text-xs flex-1" data-testid={`exp-note-${i}`} />
                  <button onClick={() => upd({ exp_details: rep.exp_details.filter((_, idx) => idx !== i) })} className="text-muted-foreground hover:text-destructive" aria-label="Remove" data-testid={`exp-del-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              {(rep.exp_details || []).length === 0 && <p className="text-xs text-muted-foreground">No expenses. Add a row.</p>}
              {(rep.exp_details || []).length > 0 && <p className="text-xs mt-2 border-t border-border pt-2">Total Expenses: <b>{fmtINR((rep.exp_details || []).reduce((s, x) => s + (x.amount || 0), 0))}</b></p>}
            </div>
            <div className="rounded-xl border border-border bg-card p-3" data-testid="denominations-block">
              <p className="font-semibold text-sm mb-2">CASH DENOMINATIONS (Total Cash Deposit)</p>
              {(() => {
                const denomTotal = (rep.denominations || []).reduce((s, x) => s + (x.amount || 0), 0);
                const deposit = Number(rep.cash_summary?.total_cash_deposit || 0);
                const diff = Math.round((denomTotal - deposit) * 100) / 100;
                if (denomTotal > 0 && deposit > 0 && Math.abs(diff) > 0.01) {
                  return (
                    <div className="mb-2 rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-xs font-semibold text-destructive" data-testid="denom-mismatch-alert">
                      Mismatch: note-count total {fmtINR(denomTotal)} vs Total Cash Deposit {fmtINR(deposit)} — {diff > 0 ? 'excess' : 'short'} by {fmtINR(Math.abs(diff))}
                    </div>
                  );
                }
                if (denomTotal > 0 && deposit > 0) {
                  return (
                    <div className="mb-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs font-semibold text-emerald-700 dark:text-emerald-400" data-testid="denom-match-badge">
                      Denominations match the Total Cash Deposit ({fmtINR(deposit)})
                    </div>
                  );
                }
                return null;
              })()}
              <table className="w-full text-xs">
                <thead className="text-muted-foreground"><tr><th className="text-left px-2 py-1 font-medium">Note ₹</th><th className="text-left px-2 py-1 font-medium">Count</th><th className="text-right px-2 py-1 font-medium">Amount ₹</th></tr></thead>
                <tbody>
                  {(rep.denominations || []).map((d, i) => (
                    <tr key={d.denom} className="border-t border-border">
                      <td className="px-2 py-1 font-bold">{d.denom}</td>
                      <td className="px-2 py-1"><Num value={d.count} onChange={(v) => {
                        const denominations = rep.denominations.map((x, idx) => idx === i ? { ...x, count: v, amount: (v || 0) * x.denom } : x);
                        upd({ denominations, denomination_total: denominations.reduce((s, x) => s + (x.amount || 0), 0) });
                      }} w="w-24" testId={`denom-count-${d.denom}`} /></td>
                      <td className="px-2 py-1 text-right font-semibold">{fmtINR(d.amount)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-border bg-yellow-400/20">
                    <td className="px-2 py-1.5 font-bold">TOTAL</td><td />
                    <td className="px-2 py-1.5 text-right font-bold" data-testid="denom-total">{fmtINR((rep.denominations || []).reduce((s, x) => s + (x.amount || 0), 0))}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="cards" className="space-y-4 mt-3">
          {[['fleet_card_txns', 'Fleet Card (BPCL) Transactions'], ['pos_txns', 'HDFC / Pine Lab POS Transactions']].map(([key, title]) => (
            <div key={key} className="rounded-xl border border-border bg-card p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="font-semibold text-sm">{title}</p>
                <Button size="sm" variant="secondary" onClick={() => upd({ [key]: [...rep[key], { _uid: uid(), tid: '', batch: '', date: '', time: '', amount: 0 }] })} data-testid={`add-${key}-row`}><Plus className="h-4 w-4 mr-1" />Row</Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-muted-foreground"><tr>{['TID No', 'Batch', 'Date', 'Time', 'Amount ₹', ''].map((h) => <th key={h} className="text-left px-2 py-1 font-medium">{h}</th>)}</tr></thead>
                  <tbody>
                    {rep[key].map((t, i) => (
                      <tr key={t._uid || i} className="border-t border-border">
                        {['tid', 'batch', 'date', 'time'].map((f) => (
                          <td key={f} className="px-2 py-1"><Input value={t[f]} onChange={(e) => upd({ [key]: rep[key].map((x, idx) => idx === i ? { ...x, [f]: e.target.value } : x) })} className="h-8 text-xs w-28" data-testid={`${key}-${f}-${i}`} /></td>
                        ))}
                        <td className="px-2 py-1"><Num value={t.amount} onChange={(v) => upd({ [key]: rep[key].map((x, idx) => idx === i ? { ...x, amount: v } : x) })} w="w-28" testId={`${key}-amt-${i}`} /></td>
                        <td className="px-2 py-1"><button onClick={() => upd({ [key]: rep[key].filter((_, idx) => idx !== i) })} className="text-muted-foreground hover:text-destructive" aria-label="Remove" data-testid={`${key}-del-${i}`}><Trash2 className="h-3.5 w-3.5" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-sm mt-2 border-t border-border pt-2">Total: <b>{fmtINR(rep[key].reduce((s, t) => s + (t.amount || 0), 0))}</b></p>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
